import React from 'react';
import { useTranslation } from 'react-i18next';

import { TButtonTypes } from '@private/components';
import { useModal } from '@private/modals';

import ModalContainer, { IModalContainerProps } from 'components/ModalContainer';

export interface IConfirmModalConfig extends IModalContainer {
  cancelText?: string;
  cancelVariant?: TButtonTypes;
  confirmBtnType?: TButtonTypes;
  confirmText?: string;
  message?: React.ReactNode;
  onClose?: () => void;
  onConfirm: () => void;
}

export interface IConfirmModalProps extends IConfirmModalConfig {
  onClose: () => void;
}

type IModalContainer = Omit<
  IModalContainerProps,
  'leftBtnText' & 'leftBtnVariant' & 'onDismiss' & 'rightBtnText' & 'rightBtnVariant'
>;

const ConfirmModal = ({
  cancelText,
  cancelVariant,
  confirmBtnType = 'primary',
  confirmText,
  message,
  onClose,
  onConfirm,
  ...rest
}: IConfirmModalProps) => {
  const { t } = useTranslation();

  return (
    <ModalContainer
      leftBtnText={cancelText || t('modals.cancel')}
      leftBtnVariant={cancelVariant}
      onDismiss={onClose}
      onLeftAction={onClose}
      onRightAction={onConfirm}
      rightBtnText={confirmText || t('modals.confirm')}
      rightBtnVariant={confirmBtnType}
      {...rest}
    >
      {message}
    </ModalContainer>
  );
};

type TUseConfirmModalResult = [(config: IConfirmModalConfig) => void, () => void];

export const useConfirmModal = (): TUseConfirmModalResult => {
  const { closeModal, showModal } = useModal();

  const showConfirmModal = React.useCallback((config: IConfirmModalConfig) => {
    showModal(
      <ConfirmModal
        {...config}
        onClose={closeModal}
        onConfirm={() => {
          config.onConfirm();
          closeModal();
        }}
      />,
    );
  }, []);

  return [showConfirmModal, closeModal];
};
